import { useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export function FakturImport({ kind, clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    try {
      const res = await api.get(`/faktur/template?kind=${kind}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `template_faktur_${kind}.xlsx`; a.click();
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("client_id", clientId); fd.append("kind", kind); fd.append("file", file);
    setBusy(true);
    try { setPv((await api.post("/faktur/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } })).data); }
    catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    setBusy(false); e.target.value = "";
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/faktur/import/commit", { client_id: clientId, kind, rows: pv.rows });
      toast.success(`${data.inserted} faktur baru, ${data.updated} diperbarui`);
      setOpen(false); setPv(null); onDone();
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <>
      <Button size="sm" className="h-9" onClick={() => setOpen(true)} data-testid={`faktur-${kind}-import`}><Upload className="h-4 w-4 mr-1" />Impor Coretax</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPv(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Impor Faktur {kind === "masukan" ? "Masukan" : "Keluaran"} dari Coretax</DialogTitle>
            <DialogDescription className="text-xs">Unggah file Excel/CSV hasil ekspor daftar faktur di Coretax (kolom Nomor Faktur Pajak, Tanggal, NPWP, Status, DPP, PPN). Faktur dengan nomor yang sama akan diperbarui, bukan digandakan.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={template} data-testid={`faktur-${kind}-template`}><Download className="h-4 w-4 mr-1" />Unduh Template</Button>
            <label className="inline-flex">
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid={`faktur-${kind}-file`} />
              <span className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input px-3 text-sm hover:bg-secondary">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Pilih File</span>
            </label>
          </div>
          {pv && (
            <div className="space-y-2" data-testid={`faktur-${kind}-preview`}>
              <p className="text-xs">{pv.total} baris · <span className="text-emerald-700">{pv.valid} valid</span> · <span className="text-sky-700">{pv.update} memperbarui faktur lama</span> · <span className="text-rose-600">{pv.invalid} error</span></p>
              <div className="overflow-auto rounded-md border border-border max-h-80">
                <Table>
                  <TableHeader><TableRow className="bg-secondary/50"><TableHead>Baris</TableHead><TableHead>Nomor Faktur</TableHead><TableHead>Tanggal</TableHead><TableHead>Nama</TableHead><TableHead>Kode</TableHead><TableHead className="text-right">PPN</TableHead><TableHead>Hasil</TableHead></TableRow></TableHeader>
                  <TableBody>{pv.rows.map((r) => (
                    <TableRow key={r.row}>
                      <TableCell className="text-xs">{r.row}</TableCell><TableCell className="num text-xs">{r.data.nomor_faktur}</TableCell>
                      <TableCell className="num text-xs">{fmtDate(r.data.tanggal)}</TableCell><TableCell className="text-xs">{r.data.nama}</TableCell>
                      <TableCell className="num text-xs">{r.data.kode}</TableCell><TableCell className="num text-right text-xs">{fmtIDR(r.data.ppn)}</TableCell>
                      <TableCell className="text-xs">{!r.valid ? <span className="text-rose-600 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />{r.errors.join("; ")}</span>
                        : r.exists ? <span className="text-sky-700 inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" />Perbarui</span>
                          : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Baru</span>}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={commit} disabled={!pv?.valid || busy} data-testid={`faktur-${kind}-commit`}>Simpan {pv?.valid || 0} Faktur</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
